/**
 * Created by Evžen Rejfíř on 22.07.2025.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, defineMessages } from 'react-intl';
import Relay from 'react-relay';
import AppPanel from '../fw/AppPanel.react';
import { wrapNotFound } from '../app/NotFoundHandler.react';
import { TARIFF_TYPE_ZAKLAD } from '../../common/model-api/TariffType';
import { displayError } from '../app/errorDisplayer';

const messages = defineMessages({
    title: {
        id: 'tariffAdd.title',
        defaultMessage: 'Adding Tariff'
    },
});

class TariffAdd extends React.Component {
    static propTypes = {
        intl: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            formData: {},
            saving: false,
        };
    }

    // Simple hash function for browser compatibility
    generateCustomerNumber = (organisationName) => {
        let hash = 0;
        for (let i = 0; i < organisationName.length; i++) {
            const char = organisationName.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString().padStart(6, '0').substring(0, 6);
    };

    handleSave = () => {
        const { params } = this.props;
        const { formData } = this.state;

        this.setState({ saving: true });

        // Generate complete data object with defaults
        const data = {
            tariffType: TARIFF_TYPE_ZAKLAD,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            active: true,
            customerNumber: this.generateCustomerNumber(this.props.user.organisation.name),
            orderNumber: (this.props.user.organisation.tariffs?.edges?.length || 0) + 1,
            ...formData
        };

        data.tariffNumber = `${data.customerNumber}-${data.orderNumber}`;

        const MutationClass = class extends Relay.Mutation {
            getMutation() {
                return Relay.QL`mutation {createTariff}`;
            }

            getVariables() {
                return {
                    tariffType: data.tariffType,
                    startDate: data.startDate,
                    endDate: data.endDate || null,
                    organisation_id: params.organisationId,
                };
            }

            getFatQuery() {
                return Relay.QL`
                    fragment on CreateTariffPayload {
                        edge {
                            node {
                                id
                                realId
                            }
                        }
                    }
                `;
            }

            getConfigs() {
                return [{
                    type: 'REQUIRED_CHILDREN',
                    children: [
                        Relay.QL`
                            fragment on CreateTariffPayload {
                                edge {
                                    node {
                                        id
                                        realId
                                    }
                                }
                            }
                        `
                    ]
                }];
            }
        };

        const mutation = new MutationClass();

        Relay.Store.commitUpdate(mutation, {
            onSuccess: (response) => {
                this.setState({ saving: false });
                /* eslint-disable-next-line no-undef */
                location.href = `/organisations/${params.organisationId}/tariffs/`;
            },
            onFailure: (transaction) => {
                this.setState({ saving: false });

                // Try to extract GraphQL error message
                let errorMessage = 'Failed to create tariff';

                try {
                    const error = transaction.getError();
                    if (error && error.res && error.res.payload && error.res.payload.errors) {
                        const graphqlError = error.res.payload.errors[0];
                        if (graphqlError && graphqlError.message) {
                            errorMessage = graphqlError.message;
                        }
                    }
                } catch (e) {
                    // Ignore error parsing errors
                }

                displayError(errorMessage);
            }
        });
    };

    handleFieldChange = (field, value) => {
        this.setState(prevState => ({
            formData: {
                ...prevState.formData,
                [field]: value
            }
        }));
    };

    render() {
        const { intl, user, params } = this.props;
        const { formData, saving } = this.state;
        const organisation = user.organisation;

        if (!organisation) {
            return (
                <AppPanel title={intl.formatMessage(messages.title)}>
                    <div className="panel-body">
                        <p>Organisation not found</p>
                    </div>
                </AppPanel>
            );
        }

        // Generate default values for new tariff
        const data = {
            tariffType: TARIFF_TYPE_ZAKLAD,
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            active: true,
            customerNumber: this.generateCustomerNumber(organisation.name),
            orderNumber: (organisation.tariffs?.edges?.length || 0) + 1,
            ...formData
        };

        data.tariffNumber = `${data.customerNumber}-${data.orderNumber}`;

        return (
            <AppPanel title={intl.formatMessage(messages.title)}>
                <div className="panel-body">
                    <h3>Adding Tariff</h3>
                    <p><strong>Organisation:</strong> {organisation.name}</p>
                    <hr />

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Tariff Type</label>
                                <select
                                    className="form-control"
                                    value={data.tariffType}
                                    onChange={(e) => this.handleFieldChange('tariffType', e.target.value)}
                                >
                                    <option value="zaklad">Základ</option>
                                    <option value="standard">Standard</option>
                                    <option value="profi">Profi</option>
                                    <option value="profi-plus">Profi+</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>Start Date</label>
                                <input
                                    className="form-control"
                                    type="date"
                                    value={data.startDate}
                                    onChange={(e) => this.handleFieldChange('startDate', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>End Date</label>
                                <input
                                    className="form-control"
                                    type="date"
                                    value={data.endDate}
                                    onChange={(e) => this.handleFieldChange('endDate', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Customer Number</label>
                                <input className="form-control" disabled value={data.customerNumber} />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Tariff Number</label>
                                <input className="form-control" disabled value={data.tariffNumber} />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group">
                                <label>Order Number</label>
                                <input className="form-control" disabled value={data.orderNumber} />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <button
                                className="btn btn-primary"
                                onClick={this.handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className="btn btn-default"
                                onClick={() => {
                                    /* eslint-disable-next-line no-undef */
                                    location.href = `/organisations/${params.organisationId}/tariffs/`;
                                }}
                                style={{ marginLeft: '10px' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(TariffAdd), (props) => {
    return props.user.organisation;
}), {
    initialVariables: {
        first: 100,
        organisationId: '',
    },
    prepareVariables(variables) {
        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                id,
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    tariffs(first: $first) {
                        edges {
                            node {
                                id,
                                realId,
                                tariffType,
                                startDate,
                                endDate,
                                active,
                                customerNumber,
                                tariffNumber,
                                createdAt,
                                updatedAt
                            }
                        }
                    }
                }
            }
        `,
    },
});
